import type { ComparisonTypes } from "../types/ComparisonTypes";
import { getKeyDifferences } from "../utils/compareInsurance";
import "../styles/DifferenceHighlighter.css";

interface DifferenceHighlighterProps {
	companies: ComparisonTypes[];
}

const DifferenceHighlighter: React.FC<DifferenceHighlighterProps> = ({
	companies,
}) => {
	// Only show when we have at least 2 companies to compare
	if (!companies || companies.length < 2) {
		return null;
	}

	const differences = getKeyDifferences(companies);

	if (differences.length === 0) {
		return (
			<div className="no-differences">
				<p>
					Ingen vesentlige forskjeller funnet mellom de valgte forsikringene.
				</p>
			</div>
		);
	}

	// Helper function to format values for display
	const formatValue = (value: unknown): string => {
		if (value === null || value === undefined) return "Ikke oppgitt";
		if (typeof value === "boolean") return value ? "Ja" : "Nei";
		return String(value);
	};

	// Helper function to get appropriate class for different values
	const getValueClass = (value: unknown): string => {
		if (value === true) return "value-positive";
		if (value === false) return "value-negative";
		if (value === null || value === undefined) return "value-unknown";
		return "";
	};

	// Group differences by section
	const sectionLabels: Record<string, string> = {
		maskinskade: "Maskinskade",
		glasskade: "Glasskade",
		parkeringsskade: "Parkeringsskade",
		kollisjon: "Kollisjon",
		leiebil: "Leiebil",
		tyveri: "Tyveri",
		brann: "Brann",
		naturskader: "Naturskader",
		bagasje_og_eiendeler: "Bagasje og eiendeler",
		ladekabelOgElbilUtstyr: "Ladekabel og elbilutstyr",
		veihjelp: "Veilhjelp",
		utforkjøring: "Utforskjøring",
		feilfylling: "Feilfylling",
	};

	// Group differences by section
	const groupedDifferences: Record<string, typeof differences> = {};

	for (const diff of differences) {
		if (!groupedDifferences[diff.section]) {
			groupedDifferences[diff.section] = [];
		}
		groupedDifferences[diff.section].push(diff);
	}

	return (
		<div className="difference-highlighter">
			<h2>Viktige forskjeller</h2>
			<p className="difference-count">
				Det er funnet {differences.length} viktige forskjeller mellom
				forsikringsselskapene.
			</p>

			{Object.entries(groupedDifferences).map(([section, diffs]) => (
				<div key={section} className="difference-section">
					<h3>{sectionLabels[section] || section}</h3>
					<table className="difference-table">
						<thead>
							<tr>
								<th>Funksjon</th>
								{companies.map((company) => (
									<th key={company.selskap}>{company.selskap}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{diffs.map((diff, index) => (
								<tr
									key={`${diff.section}-${diff.property}-${index}`}
									className="difference-row"
								>
									<td className="difference-label">{diff.label}</td>
									{companies.map((company) => {
										// Find the matching value for this company
										const valueObj = diff.values.find(
											(v) => v.company === company.selskap,
										);
										const value = valueObj ? valueObj.value : null;
										return (
											<td
												key={`${company.selskap}-${diff.property}`}
												className={`difference-value ${getValueClass(value)}`}
											>
												{formatValue(value)}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			))}
		</div>
	);
};

export default DifferenceHighlighter;
